import { DateOnlyInput } from "../components/DateOnlyInput";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { api } from "../api";
import { alphaSave } from "../ui/alpha-save";
import { ErrorState, Loading } from "../ui/State";
type Supply = {
  id: string;
  label: string;
  supplierId: string | null;
  sourceHash: string;
  reviewHash: string;
  meters: { id: string; name: string; trend: string | null }[];
  bills: {
    id: string;
    invoice_number: string;
    period_start: string;
    period_end: string;
    billed_units: string;
    unit: string;
    consumption: string | null;
    exception: string | null;
    overlap: boolean;
    sourceChanged: boolean;
  }[];
};
type Model = {
  supplies: Supply[];
  invoices: {
    id: string;
    invoice_number: string;
    document_id: string;
    supplier_organisation_id: string;
  }[];
  documents: { id: string; versionId: string }[];
};
export function UtilityExceptionsAlpha() {
  const [q] = useSearchParams(),
    building = q.get("building"),
    [data, setData] = useState<Model>(),
    [selected, setSelected] = useState(""),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    [notice, setNotice] = useState("");
  const pending = useRef<{ url: string; body: object } | null>(null);
  const load = () =>
    api<Model>("/api/alpha/utility-exceptions?building=" + building).then(
      setData,
    );
  useEffect(() => {
    setData(undefined);
    setSelected("");
    if (building) void load().catch(setError);
  }, [building]);
  async function write(url: string, body: object) {
    setBusy(true);
    setError(undefined);
    pending.current ??= {
      url,
      body: { ...body, idempotencyKey: crypto.randomUUID() },
    };
    try {
      await alphaSave(pending, pending.current.url, {
        method: "POST",
        body: JSON.stringify(pending.current.body),
      });
      pending.current = null;
      await load();
      setNotice("Saved. Review any resulting Action in the Actions register.");
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  const row = data?.supplies.find((s) => s.id === selected);
  function bill(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget),
      i = data!.invoices.find((i) => i.id === f.get("invoice"))!,
      d = data!.documents.find((d) => d.id === i.document_id)!;
    void write("/api/alpha/utility-exceptions/" + selected + "/bill", {
      building,
      sourceHash: row!.sourceHash,
      meterAssetId: f.get("meter"),
      invoiceId: i.id,
      versionId: d.versionId,
      periodStart: f.get("from"),
      periodEnd: f.get("to"),
      billedUnits: f.get("units"),
      unit: f.get("unit"),
      sourceNote: f.get("note"),
    });
  }
  function review(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    void write("/api/alpha/utility-exceptions/" + selected + "/review", {
      building,
      expectedHash: row!.reviewHash,
      note: new FormData(e.currentTarget).get("note"),
    });
  }
  return (
    <>
      <h1>Utility consumption and bill exceptions</h1>
      <p>
        Compare accepted meter observations with reviewed billed periods.
        Exceptions require investigation; they do not alter invoice or ledger
        values. This preview shows up to 200 invoices and 300 billed periods.
      </p>
      {Boolean(error) && <ErrorState error={error} />}{" "}
      {notice && <p role="status">{notice}</p>}
      {pending.current && !busy && (
        <button onClick={() => void write("", {})}>Retry the same save</button>
      )}
      {!building ? (
        <p>Open utilities from a building.</p>
      ) : !data ? (
        <Loading />
      ) : (
        <>
          <label>
            Supply
            <select
              value={selected}
              disabled={busy || !!pending.current}
              onChange={(e) => setSelected(e.target.value)}
            >
              <option value="">Choose a supply</option>
              {data.supplies.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          {!data.supplies.length&&<p className="empty">No utility supplies are recorded for this building. Add or review its supplies in Utilities before comparing billed periods.</p>}
          {row && (
            <>
              <section className="panel">
                <h2>{row.label}</h2>
                {row.meters.map((m) => (
                  <p key={m.id}>
                    {m.name}:{" "}
                    {m.trend ??
                      "No unusual usage flag in the latest intervals."}
                  </p>
                ))}
                <h3>Billed periods</h3>
                {row.bills.length ? (
                  row.bills.map((b) => (
                    <article key={b.id}>
                      <h4>
                        {b.invoice_number || "Invoice"} · {b.period_start} to{" "}
                        {b.period_end}
                      </h4>
                      <p>
                        Billed {b.billed_units} {b.unit} · Observed{" "}
                        {b.consumption ?? "unavailable"} {b.unit}
                      </p>
                      {b.exception && <p>{b.exception}</p>}
                      {b.overlap && (
                        <p>
                          Overlapping billed period: investigate a possible
                          duplicate charge.
                        </p>
                      )}
                      {b.sourceChanged && (
                        <p>
                          The bill source version changed; review its retained
                          comparison.
                        </p>
                      )}
                    </article>
                  ))
                ) : (
                  <p>No reviewed bill periods linked.</p>
                )}
              </section>
              <form className="panel form" onSubmit={bill}>
                <fieldset disabled={busy || !!pending.current}>
                  <legend>Link a reviewed bill period</legend>
                  <label>
                    Meter
                    <select name="meter" required>
                      {row.meters.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Supplier invoice
                    <select name="invoice" required>
                      <option value="">Choose invoice</option>
                      {data.invoices
                        .filter(
                          (i) => i.supplier_organisation_id === row.supplierId,
                        )
                        .map((i) => (
                          <option key={i.id} value={i.id}>
                            {i.invoice_number || "Unnumbered invoice"}
                          </option>
                        ))}
                    </select>
                  </label>
                  <label>
                    Opening reading date
                    <DateOnlyInput name="from" required />
                  </label>
                  <label>
                    Closing reading date
                    <DateOnlyInput name="to" required />
                  </label>
                  <label>
                    Billed usage
                    <input
                      name="units"
                      inputMode="decimal"
                      required
                      pattern="[0-9]+([.][0-9]{1,6})?"
                    />
                  </label>
                  <label>
                    Meter unit, exactly as recorded
                    <input
                      name="unit"
                      required
                      maxLength={30}
                      placeholder="kWh"
                    />
                  </label>
                  <label>
                    Reviewed bill reference and period interpretation
                    <textarea
                      name="note"
                      required
                      minLength={10}
                      maxLength={2000}
                    />
                  </label>
                  <button>Retain billed period</button>
                </fieldset>
              </form>
              <form className="panel form" onSubmit={review}>
                <fieldset disabled={busy || !!pending.current}>
                  <legend>Prepare exception review</legend>
                  <label>
                    What needs investigating?
                    <textarea
                      name="note"
                      required
                      minLength={10}
                      maxLength={2000}
                    />
                  </label>
                  <button>Create review Action</button>
                </fieldset>
              </form>
            </>
          )}
        </>
      )}
    </>
  );
}
